import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import CopyValueButton from "./CopyValueButton";
import { getTranscribeJobAudioUrl, getTranscribeJobSegments } from "./patientApi";
import { supportInsetPanelAuto } from "./supportStyles";
import {
  buildSpeakerBlocks,
  findActiveSegmentIndex,
} from "./transcriptSegmentAlign";
import {
  getJobTranscriptText,
  normalizeTranscriptionJobs,
  sortTranscriptionJobs,
} from "./transcriptionJobUtils";

export const formatAudioDuration = (audioLength) => {
  if (audioLength == null || audioLength === "") return "—";
  const seconds = Number(audioLength);
  if (Number.isFinite(seconds)) return `${seconds}s`;
  return String(audioLength);
};

export const CopyJobIdButton = ({ jobId }) => (
  <CopyValueButton value={jobId} label="Copy job name" />
);

const useTranscribeJobAudio = (transcribeJobName, enabled = true) => {
  const [audioUrl, setAudioUrl] = useState(null);
  const [loadState, setLoadState] = useState("idle");
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!enabled) {
      setAudioUrl(null);
      setLoadState("idle");
      setError(null);
      return;
    }

    if (!transcribeJobName) {
      setAudioUrl(null);
      setLoadState("error");
      setError("Missing transcribe job name");
      return;
    }

    let cancelled = false;
    setLoadState("loading");
    setError(null);
    setAudioUrl(null);

    (async () => {
      const result = await getTranscribeJobAudioUrl(transcribeJobName);
      if (cancelled) return;

      if (result.error) {
        setLoadState("error");
        setError(result.error);
        return;
      }

      setAudioUrl(result.audio_url);
      setLoadState("ready");
    })();

    return () => {
      cancelled = true;
    };
  }, [transcribeJobName, enabled]);

  return { audioUrl, loadState, error };
};

// Only one recording should be audible at a time. Tracking the playing element
// at module scope keeps sibling cards independent without a shared context.
let currentlyPlayingAudio = null;

const pauseOtherPlayers = (element) => {
  if (currentlyPlayingAudio && currentlyPlayingAudio !== element) {
    currentlyPlayingAudio.pause();
  }
  currentlyPlayingAudio = element;
};

export const TranscriptionAudioPlayer = ({
  job,
  enabled = true,
  audioRef,
  onTimeUpdate,
}) => {
  const { audioUrl, loadState, error } = useTranscribeJobAudio(job.transcribe_job_name, enabled);

  if (!job.transcribe_job_name) {
    return <p className="text-xs text-red-600">Missing transcribe job name</p>;
  }

  if (loadState === "idle" || loadState === "loading") {
    return <p className="text-xs text-gray-500">Loading audio…</p>;
  }

  if (loadState === "error") {
    return <p className="text-xs text-red-600">{error}</p>;
  }

  if (!audioUrl) return null;

  return (
    <audio
      ref={audioRef}
      controls
      preload="metadata"
      className="w-full h-9"
      src={audioUrl}
      onTimeUpdate={onTimeUpdate}
      onPlay={(event) => pauseOtherPlayers(event.currentTarget)}
    >
      Your browser does not support the audio element.
    </audio>
  );
};

export const useSortedTranscriptionJobs = (transcription) =>
  useMemo(
    () => sortTranscriptionJobs(normalizeTranscriptionJobs(transcription)),
    [transcription]
  );

/** Pause auto-scroll for this long after the user scrolls the transcript. */
const AUTO_SCROLL_PAUSE_MS = 4000;

const useTranscribeJobSegments = (transcribeJobName, enabled = true) => {
  const [segments, setSegments] = useState([]);

  useEffect(() => {
    if (!enabled || !transcribeJobName) {
      setSegments([]);
      return;
    }

    let cancelled = false;

    (async () => {
      const result = await getTranscribeJobSegments(transcribeJobName);
      if (cancelled) return;
      // Missing or failed segments are not an error state: the card falls back
      // to the plain transcript, so there is nothing to surface to the user.
      setSegments(result.error ? [] : result.segments || []);
    })();

    return () => {
      cancelled = true;
    };
  }, [transcribeJobName, enabled]);

  return segments;
};

const SegmentButton = ({ segment, isActive, onSeek }) => (
  <button
    type="button"
    onClick={() => onSeek(segment)}
    aria-current={isActive ? "true" : undefined}
    title="Jump to this moment"
    className={`text-left rounded px-0.5 -mx-0.5 transition-colors focus:outline-none focus:ring-1 focus:ring-ktx-purple ${
      isActive
        ? "bg-ktx-purple/20 text-gray-900"
        : "hover:bg-gray-200/70 text-inherit"
    }`}
  >
    {segment.text}
  </button>
);

const TranscriptRecordingCard = ({ job, index, loadAudio = true, bodyTextClass = "text-sm" }) => {
  const audioRef = useRef(null);
  const transcriptRef = useRef(null);
  const autoScrollPausedUntilRef = useRef(0);
  const [activeIndex, setActiveIndex] = useState(-1);

  const text = getJobTranscriptText(job);
  const jobName = job.transcribe_job_name;
  const segments = useTranscribeJobSegments(jobName, loadAudio);
  const isSynced = segments.length > 0;

  // Alignment returns copies, so carry the source index along to identify the
  // active segment by position rather than object identity.
  const indexedSegments = useMemo(
    () => segments.map((segment, position) => ({ ...segment, index: position })),
    [segments]
  );

  const blocks = useMemo(
    () => (isSynced ? buildSpeakerBlocks(indexedSegments, text) : []),
    [isSynced, indexedSegments, text]
  );
  const hasSpeakerLabels = blocks.some((block) => block.speaker);

  const meta = [
    job.audio_length != null && formatAudioDuration(job.audio_length),
    job.job_start_time,
  ]
    .filter(Boolean)
    .join(" · ");

  // Runs ~4x/second during playback, so resolve with a binary search and only
  // set state when the highlighted segment actually changes.
  const handleTimeUpdate = useCallback(
    (event) => {
      if (!isSynced) return;
      const nextIndex = findActiveSegmentIndex(segments, event.currentTarget.currentTime);
      setActiveIndex((current) => (current === nextIndex ? current : nextIndex));
    },
    [isSynced, segments]
  );

  const handleSeek = useCallback((segment) => {
    const audio = audioRef.current;
    if (!audio) return;
    // Clicking a segment is an explicit request to follow along again.
    autoScrollPausedUntilRef.current = 0;
    audio.currentTime = segment.start;
    const played = audio.play();
    if (played?.catch) played.catch(() => {});
  }, []);

  const pauseAutoScroll = useCallback(() => {
    autoScrollPausedUntilRef.current = Date.now() + AUTO_SCROLL_PAUSE_MS;
  }, []);

  // Keep the active segment visible, but never fight a user who is reading
  // elsewhere in the transcript.
  useEffect(() => {
    if (activeIndex < 0) return;
    if (Date.now() < autoScrollPausedUntilRef.current) return;

    const pane = transcriptRef.current;
    const active = pane?.querySelector('[data-active-segment="true"]');
    if (!active) return;

    const paneBox = pane.getBoundingClientRect();
    const activeBox = active.getBoundingClientRect();
    const isVisible =
      activeBox.top >= paneBox.top && activeBox.bottom <= paneBox.bottom;
    if (isVisible) return;

    active.scrollIntoView({ block: "nearest", behavior: "smooth" });
  }, [activeIndex]);

  const renderSegments = (list) =>
    list.map((segment) => {
      const isActive = segment.index === activeIndex;
      return (
        <React.Fragment key={segment.index}>
          <span data-active-segment={isActive ? "true" : undefined}>
            <SegmentButton segment={segment} isActive={isActive} onSeek={handleSeek} />
          </span>{" "}
        </React.Fragment>
      );
    });

  const renderTranscriptBody = () => {
    if (isSynced && hasSpeakerLabels) {
      return (
        <div className="space-y-2">
          {blocks.map((block, blockIndex) => (
            <div key={`${block.speaker}-${blockIndex}`}>
              <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                {block.speaker}
              </p>
              <p>{renderSegments(block.segments)}</p>
            </div>
          ))}
        </div>
      );
    }

    if (isSynced) {
      return <p>{renderSegments(indexedSegments)}</p>;
    }

    if (text) {
      return <p className="whitespace-pre-wrap">{text}</p>;
    }

    return (
      <p className="text-gray-500 italic">No transcript text for this recording.</p>
    );
  };

  return (
    <article className={`${supportInsetPanelAuto} flex flex-col px-3 pt-2.5 pb-2`}>
      {/* Sticky so the player stays reachable whether the transcript pane or the
          surrounding column is the thing being scrolled. */}
      <header className="sticky top-0 z-10 shrink-0 space-y-1 rounded-t-md bg-white pb-2">
        <div className="flex items-center justify-between gap-2 min-w-0">
          <p className="text-[10px] leading-none text-gray-400 shrink-0">
            Recording {index + 1}
            {meta ? ` · ${meta}` : ""}
          </p>
          {jobName && (
            <div className="flex items-center gap-1 min-w-0">
              <p
                className="text-[10px] leading-none text-gray-500 truncate text-right"
                title={jobName}
              >
                {jobName}
              </p>
              <CopyJobIdButton jobId={jobName} />
            </div>
          )}
        </div>

        {jobName && (
          <TranscriptionAudioPlayer
            job={job}
            enabled={loadAudio}
            audioRef={audioRef}
            onTimeUpdate={handleTimeUpdate}
          />
        )}
        {jobName && !isSynced && text && (
          <p className="text-[10px] leading-none text-gray-400">
            Click-to-seek unavailable for this recording.
          </p>
        )}
      </header>

      <div
        ref={transcriptRef}
        onWheel={pauseAutoScroll}
        onTouchMove={pauseAutoScroll}
        className={`${bodyTextClass} flex-1 overflow-y-auto max-h-[45vh] border-t border-gray-100 pt-2`}
      >
        {renderTranscriptBody()}
      </div>
    </article>
  );
};

export const StitchedTranscriptPanel = ({
  sortedJobs,
  className = "",
  embedded = false,
  loadAudio = true,
  bodyTextClass = "text-sm leading-relaxed text-gray-800",
}) => {
  const transcriptCount = sortedJobs.filter((job) => getJobTranscriptText(job)).length;
  const hasGaps = sortedJobs.length > 0 && transcriptCount < sortedJobs.length;

  if (sortedJobs.length === 0) {
    return <p className={`${bodyTextClass} text-gray-500`}>No recordings found.</p>;
  }

  return (
    <section className={className}>
      {!embedded && (
        <h4 className="text-xs font-semibold text-gray-800 mb-1.5">Transcript</h4>
      )}
      {hasGaps && (
        <p className="text-[11px] text-amber-700 mb-2">
          Some recordings have no transcript text.
        </p>
      )}
      <div className="space-y-4">
        {sortedJobs.map((job, index) => (
          <TranscriptRecordingCard
            key={job.transcribe_job_name || index}
            job={job}
            index={index}
            loadAudio={loadAudio}
            bodyTextClass={bodyTextClass}
          />
        ))}
      </div>
    </section>
  );
};
